/**
 * FALLBACK ElevenLabs voice selection for protagonists.
 *
 * NOT the primary path. The catalogue owns which voice a character speaks in —
 * `transmissions.elevenlabs_agent_id`, read by `protagonistVoiceLookup.ts`. This
 * module is only reached for a row with no value set, or a caller with no id.
 *
 * THIS FILE IS NO LONGER A PORT OF VoiceConfig.swift. It used to say it was "a
 * faithful TypeScript port of the iOS app's VoiceConfig.swift, so the website
 * and the iOS app pick the SAME voice" — that became false when the app stopped
 * deriving voices and started reading the same column. Both clients now agree by
 * reading one row, not by keeping two copies of a rule in step. Do not re-sync
 * this file to the Swift one; there is nothing left there to copy.
 *
 * Why deriving was abandoned on both sides: measured across all 147 protagonists,
 * name-and-archetype derivation put 44 of them in the wrong gender, while the
 * catalogue column was wrong for none. It also cannot express the bespoke
 * Spotlight voices (Paul Atreides, Arthur Dent, Winston Smith, Hari Seldon,
 * Ariane Emory, Mona Lisa, A. Square, Bobby Newmark, Commander Norton), which
 * exist only in the column.
 *
 * Kept deliberately pure and dependency-free so it can be compiled and exercised
 * on its own:
 *   npx esbuild src/services/protagonistVoices.ts --bundle --format=esm --outfile=pv.mjs
 *
 * Voice IDs below are the shared palette and must stay identical to the app's.
 */

export interface ProtagonistVoiceInput {
  /** Protagonist (character) name, e.g. "Paul Atreides". */
  protagonist?: string | null;
  title: string;
  author: string;
  protagonistIntro?: string | null;
  id?: string | number | null;
}

// ── ElevenLabs pre-made voice IDs ───────────────────────────────────────────
// Owned ElevenLabs Voice-Design voices, mirroring the iOS app's palette.
//
// These were ElevenLabs *Default* voices (Rachel, Bella, Adam, Antoni…) until
// 2026-08-09. Two things were wrong with that. ElevenLabs deletes all Default
// voices on 2026-12-31 — but more urgently, `elevenlabs-tts` rejects any voiceId
// outside its OWNED_VOICES allowlist and substitutes the fallback, and not one
// of the thirteen Defaults was on it. Every protagonist on this site was already
// speaking in the same fallback voice; the variety below was dead code.
//
// The names are archetypes, not ElevenLabs voice names, so they stay honest if
// the underlying voice is ever re-cut. Source of truth for the palette is the
// app repo: Author Chat/ModelsVoiceConfig.swift.
// Female
const CLEAR_FEMALE = "PsMguEzDWEAlyHfbHjzN";   // clear, composed
const WARM_FEMALE = "v7mXTR2B02XjciAR3xKE";    // mature, reassuring
const BRIGHT_FEMALE = "aNeVVPJdAUxhmlTB8te0";  // young, playful
const VELVET_FEMALE = "fBZBLrStVXYlUqayF5yY";  // rich, dramatic
// Male
const DEEP_MALE = "R2zSOfEusnmHl1ag74BW";      // deep, resonant
const WARM_MALE = "XTAQILhUhq4EWisW1Eb0";      // warm storyteller
const EASY_MALE = "hPhgAN4CVXq8NzOL9ouL";      // relaxed, younger
const ELDER_MALE = "f6DSXS7MDcSvb4IoDCFy";     // older, wise
const INTENSE_MALE = "lww9Gx6SX6X4grwQUeLa";   // fierce, edged
const BRIT_MALE = "qsqQnUgWGHLi9sppLdt9";      // British broadcaster
// Neutral house voice — used when gender cannot be inferred.
export const HOUSE_VOICE = "nSvmpnTylgxUN6WAWuLn";

export const DEFAULT_VOICE_ID = HOUSE_VOICE;

const FALLBACK_POOL = [CLEAR_FEMALE, WARM_FEMALE, BRIGHT_FEMALE, VELVET_FEMALE, DEEP_MALE, WARM_MALE, EASY_MALE, ELDER_MALE, EASY_MALE, INTENSE_MALE, EASY_MALE, BRIT_MALE, WARM_MALE];

type Archetype = "cyberpunk" | "space" | "oppression" | "philosophy" | "default";

const ARCHETYPE_POOLS: Record<Archetype, string[]> = {
  cyberpunk: [INTENSE_MALE, BRIT_MALE, VELVET_FEMALE, DEEP_MALE],
  space: [WARM_MALE, EASY_MALE, WARM_MALE, CLEAR_FEMALE, BRIGHT_FEMALE],
  oppression: [DEEP_MALE, VELVET_FEMALE, INTENSE_MALE, WARM_FEMALE],
  philosophy: [BRIT_MALE, WARM_MALE, WARM_FEMALE, CLEAR_FEMALE],
  default: [CLEAR_FEMALE, DEEP_MALE, WARM_FEMALE, WARM_MALE, BRIGHT_FEMALE, EASY_MALE],
};

// Display labels. Nine male archetypes collapsed onto six owned voices, so the
// old ElevenLabs names (Josh/Sam/Charlie all meaning one voice now) would have
// been duplicate keys with last-one-wins — silently mislabelling two of three.
const VOICE_NAMES: Record<string, string> = {
  [CLEAR_FEMALE]: "Clear", [WARM_FEMALE]: "Warm", [BRIGHT_FEMALE]: "Bright", [VELVET_FEMALE]: "Velvet",
  [DEEP_MALE]: "Deep", [WARM_MALE]: "Storyteller", [EASY_MALE]: "Easy",
  [ELDER_MALE]: "Elder", [INTENSE_MALE]: "Intense", [BRIT_MALE]: "British",
  [HOUSE_VOICE]: "House",
};

// Manual overrides (normalized protagonist name → voice ID). Ported 1:1 from iOS.
const MANUAL_OVERRIDES_RAW: Record<string, string> = {
  // Classic Literature — Female
  "elizabeth bennet": CLEAR_FEMALE, "jane eyre": VELVET_FEMALE, "scout finch": BRIGHT_FEMALE, offred: WARM_FEMALE,
  "hester prynne": VELVET_FEMALE, "dorothea brooke": CLEAR_FEMALE, "catherine earnshaw": WARM_FEMALE,
  "anna karenina": VELVET_FEMALE, "emma woodhouse": CLEAR_FEMALE, "jo march": BRIGHT_FEMALE, "lucy pevensie": BRIGHT_FEMALE,
  // Classic Literature — Male
  heathcliff: INTENSE_MALE, "mr. darcy": BRIT_MALE, "fitzwilliam darcy": BRIT_MALE, "sherlock holmes": BRIT_MALE,
  "jay gatsby": WARM_MALE, "atticus finch": DEEP_MALE, pip: EASY_MALE, "jean valjean": DEEP_MALE,
  "edmond dantès": ELDER_MALE, "edmond dantes": ELDER_MALE, raskolnikov: WARM_MALE, ishmael: DEEP_MALE,
  "holden caulfield": EASY_MALE,
  // Science Fiction — Male
  // Deckard has a purpose-cut voice in the app repo (ModelsVoiceConfig.swift);
  // it is on the OWNED_VOICES allowlist, so the site can use the same one.
  "rick deckard": "CwQ2eXvwwoKEXMXjuDW0",
  "paul atreides": EASY_MALE, "hari seldon": WARM_MALE, "ender wiggin": EASY_MALE, case: INTENSE_MALE,
  "henry case": INTENSE_MALE, "winston smith": DEEP_MALE, "arthur dent": EASY_MALE, "ford prefect": EASY_MALE,
  "gully foyle": INTENSE_MALE, "elijah baley": WARM_MALE, "valentine michael smith": EASY_MALE, "a. square": BRIT_MALE,
  // Science Fiction — Female
  breq: VELVET_FEMALE, murderbot: VELVET_FEMALE, "lucinda carlyle": VELVET_FEMALE, "anzha lyu mitethe": VELVET_FEMALE,
  "ariane emory": VELVET_FEMALE, "mona lisa": BRIGHT_FEMALE,
  // Science Fiction — Male
  "genly ai": WARM_MALE, shevek: WARM_MALE,
  // Fantasy — Male
  "frodo baggins": EASY_MALE, frodo: EASY_MALE, aragorn: DEEP_MALE, "bilbo baggins": WARM_MALE, bilbo: WARM_MALE,
  kvothe: EASY_MALE, fitz: EASY_MALE, "fitzchevalric farseer": EASY_MALE, rincewind: EASY_MALE, "sam vimes": DEEP_MALE,
  // Fantasy — Female / other
  ged: EASY_MALE, "lyra belacqua": BRIGHT_FEMALE, lyra: BRIGHT_FEMALE,
};

/** diacritic-insensitive, lowercase, alphanumeric-only, whitespace-collapsed. */
function normalize(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/\p{M}/gu, "") // strip combining diacritical marks
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

const MANUAL_OVERRIDES: Record<string, string> = Object.fromEntries(
  Object.entries(MANUAL_OVERRIDES_RAW).map(([k, v]) => [normalize(k), v])
);

// ── Gender-aware fallback for protagonists NOT in the curated map ────────────
// Which voices read as female vs male, so an inferred gender can pick a
// same-gender voice instead of a random one from a mixed archetype pool.
const FEMALE_VOICES = new Set<string>([CLEAR_FEMALE, WARM_FEMALE, BRIGHT_FEMALE, VELVET_FEMALE]);
const MALE_VOICES = new Set<string>([DEEP_MALE, WARM_MALE, EASY_MALE, ELDER_MALE, EASY_MALE, INTENSE_MALE, EASY_MALE, BRIT_MALE, WARM_MALE]);

// Compact given-name → gender lookup. Common English + international first names
// plus a few SF/literary protagonists in the catalog. Deliberately lightweight
// and conservative: unisex names (Alex, Sam, Jordan, Robin, Charlie…) are
// intentionally omitted so we never guess wrong — unknown names fall through to
// the archetype pool (previous behaviour).
const NAME_GENDER_RAW: Record<string, "female" | "male"> = {
  // Female — common
  mary: "female", patricia: "female", jennifer: "female", linda: "female", elizabeth: "female",
  barbara: "female", susan: "female", jessica: "female", sarah: "female", karen: "female",
  nancy: "female", lisa: "female", margaret: "female", sandra: "female", ashley: "female",
  kimberly: "female", emily: "female", donna: "female", michelle: "female", carol: "female",
  amanda: "female", dorothy: "female", melissa: "female", deborah: "female", stephanie: "female",
  rebecca: "female", sharon: "female", laura: "female", cynthia: "female", kathleen: "female",
  amy: "female", angela: "female", anna: "female", ruth: "female", brenda: "female",
  pamela: "female", nicole: "female", katherine: "female", samantha: "female", christine: "female",
  catherine: "female", virginia: "female", carolyn: "female", janet: "female", maria: "female",
  heather: "female", diane: "female", julie: "female", victoria: "female", kelly: "female",
  christina: "female", joan: "female", evelyn: "female", judith: "female", megan: "female",
  alice: "female", julia: "female", grace: "female", danielle: "female", charlotte: "female",
  natalie: "female", theresa: "female", gloria: "female", kathryn: "female", hannah: "female",
  olivia: "female", sophia: "female", emma: "female", ava: "female", isabella: "female",
  mia: "female", abigail: "female", ella: "female", scarlett: "female", luna: "female",
  clara: "female", jane: "female", molly: "female", ellen: "female", rose: "female",
  freya: "female", astrid: "female", ingrid: "female", esme: "female", eve: "female",
  // Female — given names of SF AUTHORS in this catalogue. Authors reach this
  // table through their own name (voice mode is reused for them), so a gap here
  // is both audible and readable — it is what left Ursula K. Le Guin as "their
  // own work". Unisex forms (Pat, Sam, Alex) stay out by the rule above.
  ursula: "female", octavia: "female", vonda: "female", nnedi: "female",
  becky: "female", connie: "female", naomi: "female", tamsyn: "female",
  annalee: "female", susanna: "female", martha: "female", kate: "female",
  // Female — SF/literary
  cordelia: "female", arya: "female", hermione: "female", katniss: "female", leia: "female",
  eowyn: "female", galadriel: "female", arwen: "female", chani: "female", alia: "female",
  binti: "female", rydra: "female", mahit: "female", marghe: "female", ravna: "female",
  fatma: "female", jessamine: "female", offred: "female", nyx: "female", zara: "female",
  // Male — common
  james: "male", robert: "male", john: "male", michael: "male", william: "male",
  david: "male", richard: "male", joseph: "male", thomas: "male", charles: "male",
  christopher: "male", matthew: "male", anthony: "male", mark: "male", donald: "male",
  steven: "male", paul: "male", andrew: "male", joshua: "male", kenneth: "male",
  kevin: "male", brian: "male", george: "male", timothy: "male", ronald: "male",
  jason: "male", edward: "male", jeffrey: "male", ryan: "male", jacob: "male",
  gary: "male", nicholas: "male", eric: "male", jonathan: "male", stephen: "male",
  larry: "male", justin: "male", scott: "male", brandon: "male", benjamin: "male",
  gregory: "male", alexander: "male", patrick: "male", frank: "male", raymond: "male",
  jack: "male", dennis: "male", jerry: "male", tyler: "male", aaron: "male",
  jose: "male", henry: "male", adam: "male", douglas: "male", nathan: "male",
  peter: "male", zachary: "male", kyle: "male", walter: "male", harold: "male",
  jeremy: "male", ethan: "male", carl: "male", keith: "male", roger: "male",
  gerald: "male", arthur: "male", terry: "male", sean: "male", lawrence: "male",
  oliver: "male", liam: "male", noah: "male", elijah: "male", lucas: "male",
  mason: "male", logan: "male", jackson: "male", owen: "male", wyatt: "male",
  isaac: "male", gabriel: "male", julian: "male", leo: "male", theodore: "male",
  victor: "male", simon: "male", ivan: "male", dmitri: "male", sergei: "male",
  hiro: "male", takeshi: "male", kenji: "male",
  // Diminutives. Absent before, which mattered once the lookup began reading the
  // GIVEN name only: "Jim Holden" had been resolving off the surname "holden".
  // All unambiguously male; unisex short forms (Sam, Alex, Charlie) stay out.
  jim: "male", jimmy: "male", joe: "male", tom: "male", dave: "male", mike: "male",
  bill: "male", billy: "male", bobby: "male", nick: "male", pete: "male", phil: "male",
  harry: "male", danny: "male", barry: "male", andy: "male", tony: "male", ron: "male",
  // "philip" was absent while "phil" was present, so Philip K. Dick resolved to
  // no gender at all and spoke in the neutral house voice — on the map's new
  // Speak button AND on his own page, which routes voice the same way. Authors
  // reach this table through their own name, so a gap here is audible.
  philip: "male", phillip: "male",
  // Male — SF/literary
  winston: "male", atticus: "male", holden: "male", heathcliff: "male", ender: "male",
  hari: "male", frodo: "male", bilbo: "male", aragorn: "male", kvothe: "male",
  fitz: "male", rincewind: "male", francis: "male", pham: "male", gully: "male",
  shevek: "male", genly: "male", ford: "male", bob: "male", kovacs: "male",
};

const NAME_GENDER: Record<string, "female" | "male"> = Object.fromEntries(
  Object.entries(NAME_GENDER_RAW).map(([k, v]) => [normalize(k), v])
);

type Gender = "female" | "male" | "neutral";

/**
 * Explicit gender per catalogue protagonist — the authoritative source, checked
 * before any name heuristic.
 *
 * WHY THIS EXISTS: a first-name lookup cannot work for this catalogue. Measured
 * against all 147 protagonists in `transmissions`, 95 of them (65%) had NO
 * inferable gender — invented SF names (Breq, Essun, Yatima, Trurl, D-503,
 * Murderbot), epithets (The Biologist, The Consul, The Narrator, The Time
 * Traveller), and non-Western names (Luo Ji, Ye Wenjie, Nobusuke Tagomi, Kaaro).
 * Those all fell through to a gender-mixed archetype pool, i.e. a coin flip, and
 * 44 characters were audibly the wrong gender: 15 women in male voices (Gina
 * Aiesi, Nell, Klara, Snake, Red, Louise Banks…) and 29 men in female voices
 * (Severian, Miles Vorkosigan, Lazarus Long, Joe Chip…).
 *
 * Gender is a fact about the book, not about the spelling of a name, so it is
 * recorded rather than guessed. "neutral" is for characters the text itself does
 * not gender (Murderbot uses "it"; Yatima uses "ve"), and those get HOUSE_VOICE
 * — EXCEPT where MANUAL_OVERRIDES already names a voice, which still wins.
 * Murderbot is the live example: recorded neutral here, but the curated override
 * keeps it on VELVET_FEMALE, matching the iOS app.
 */
const PROTAGONIST_GENDER_RAW: Record<string, Gender> = {
  // Women
  "anzha lyu mitethe": "female", "ariane emory": "female", "avice benner cho": "female",
  breq: "female", "cheng xin": "female", "dana franklin": "female", "elma york": "female",
  essun: "female", "gideon nav": "female", "gillian baskin": "female", "gina aiesi": "female",
  "juliette nichols": "female", "kathy h": "female", "kel cheris": "female",
  "kirsten raymonde": "female", "kivrin engle": "female", klara: "female",
  "lauren olamina": "female", "louise banks": "female", nell: "female", red: "female",
  "rosemary harper": "female", "roxy monke": "female", snake: "female",
  "the biologist": "female", "ye wenjie": "female", "janet evason": "female",
  "marghe taishan": "female", "fatma el sha arawi": "female", "mahit dzmare": "female",
  "ravna bergsndot": "female", "rydra wong": "female", "susan calvin": "female",
  "patricia delfine": "female", binti: "female", offred: "female", "mona lisa": "female",
  // Men
  "a square": "male", "abelard lindsay": "male", "anderson lake": "male", "barry sutton": "male",
  "bernard marx": "male", "bill masen": "male", "billy pilgrim": "male", "bobby newmark": "male",
  "bora horza gobuchul": "male", campion: "male", "cheradenine zakalwe": "male",
  "commander norton": "male", "d 503": "male", "dan sylveste": "male", "danny dunn": "male",
  "darrow au andromedus": "male", dodd: "male", "dr vannevar morgan": "male",
  "emilio sandoz": "male", "enoch wallace": "male", "fraa erasmas": "male", gerry: "male",
  "guy montag": "male", "harry adams": "male", "horselover fat": "male", "ijon tichy": "male",
  "isherwood williams": "male", "jarvis hilton": "male", "jernau morat gurgeh": "male",
  "jimmy snowman": "male", "joam donn": "male", "joe chip": "male", "joe fernwright": "male",
  kaaro: "male", "kris kelvin": "male", "lazarus long": "male", "louis wu": "male",
  "luo ji": "male", "malachi constant": "male", "martin springfield": "male",
  "miles vorkosigan": "male", "nobusuke tagomi": "male", "norman house": "male",
  piranesi: "male", "raul endymion": "male", "red schuhart": "male", "rick deckard": "male",
  "robinette broadhead": "male", "rod blaine": "male", "rod mcban": "male", rushdi: "male",
  sam: "male", severian: "male", "siri keeton": "male", "the consul": "male",
  "the narrator": "male", "the navigator": "male", "the time traveller": "male",
  "trevor jamieson": "male", trurl: "male", "tyador borlu": "male", "vergil ulam": "male",
  "wade watts": "male", "will kuno monet": "male", "zac hobson": "male",
  // Both of these were previously read as WOMEN off their surnames: the old
  // token scan matched "Grace" and "Kelly", which are female given names in
  // NAME_GENDER. Recorded here, and the scan below now only reads given names.
  "ryland grace": "male", "manuel garcia o kelly davis": "male",
  // Both had been resolving off a surname that happens to be a given name in
  // NAME_GENDER ("holden" from Holden Caulfield, "mason"). Right answer, wrong
  // reason — recorded here so it holds now the lookup reads given names only.
  "jim holden": "male", "holsten mason": "male",
  // Ungendered in the text
  murderbot: "neutral", yatima: "neutral",
  // "Zhan" on Choices of One looks like a bad extraction of the AUTHOR's name
  // (Timothy Zahn). Left neutral rather than guessed; fix the row, not this map.
  zhan: "neutral",
};

const PROTAGONIST_GENDER: Record<string, Gender> = Object.fromEntries(
  Object.entries(PROTAGONIST_GENDER_RAW).map(([k, v]) => [normalize(k), v])
);

const FEMALE_HONORIFICS = /\b(mrs|ms|miss|lady|madam|madame|mother|sister|queen|princess|empress|duchess|dame|aunt|nun)\b/;
const MALE_HONORIFICS = /\b(mr|mister|sir|lord|father|brother|king|prince|emperor|duke|uncle|monk|friar)\b/;

// Leading words that are ranks/titles rather than given names, so the given-name
// lookup skips past them. Includes in-world titles this catalogue actually uses
// ("Fraa" in Anathem, "Commander" in Rendezvous with Rama).
const TITLE_TOKENS = new Set([
  "mr", "mister", "mrs", "ms", "miss", "dr", "doctor", "prof", "professor",
  "sir", "lady", "lord", "dame", "madam", "madame",
  "captain", "capt", "commander", "cmdr", "admiral", "general", "colonel",
  "major", "sergeant", "lieutenant", "chief",
  "father", "brother", "sister", "mother", "monk", "friar", "nun", "saint", "st",
  "king", "queen", "prince", "princess", "emperor", "empress", "duke", "duchess",
  "fraa", "suur", "the",
]);

/**
 * Best-effort gender of a protagonist from their name: scans name tokens against
 * NAME_GENDER (handles titles like "Professor Edward…"), then falls back to
 * honorific keywords. Returns null when unknown — caller keeps prior behaviour.
 */
function inferGender(p: ProtagonistVoiceInput): Gender | null {
  const raw = p.protagonist?.trim();
  if (!raw) return null;
  const norm = normalize(raw);
  // Recorded fact about the character wins over any guess from the spelling.
  const known = PROTAGONIST_GENDER[norm];
  if (known) return known;

  // Read the GIVEN name only — the first token that is not a title.
  //
  // This used to scan every token and take the first hit anywhere in the name,
  // which meant surnames were read as given names: "Ryland Grace" matched
  // "grace" and "Manuel Garcia O'Kelly-Davis" matched "kelly", so two men were
  // assigned female voices. Titles are skipped so "Professor Edward L. Hastings"
  // still resolves off "edward", and anything a title alone can settle (Father,
  // Brother, Lady…) is caught by the honorific test below.
  const tokens = norm.split(" ").filter((t) => t && !TITLE_TOKENS.has(t));
  const g = NAME_GENDER[tokens[0]];
  if (g) return g;
  if (FEMALE_HONORIFICS.test(norm)) return "female";
  if (MALE_HONORIFICS.test(norm)) return "male";
  return null;
}

/** djb2 hash → stable index, so the same protagonist always maps to the same pool voice. */
function stableIndex(seed: string, modulo: number): number {
  if (modulo <= 0) return 0;
  let hash = 5381;
  for (let i = 0; i < seed.length; i++) {
    hash = (((hash << 5) + hash) + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % modulo;
}

/**
 * Archetype terms match at a WORD BOUNDARY, not as bare substrings.
 *
 * The previous `corpus.includes(term)` was true for any text merely containing
 * the letters. "ai" matched "said", "again", "chair", "air" — and the name
 * "Gina Aiesi" — so a large share of the catalogue was classified "cyberpunk"
 * by accident. The cyberpunk pool is three-quarters male voices, so that
 * misclassification actively pushed characters toward male-sounding voices.
 *
 * Most terms are prefixes on purpose (cyber → cyberpunk/cyberspace), so they
 * anchor to the start of a word only. The short ambiguous ones ("ai", "war")
 * are additionally anchored at the end so they must stand alone.
 */
const ARCHETYPE_TERMS: [Exclude<Archetype, "default">, RegExp][] = [
  ["cyberpunk", /\b(?:cyber|neon|hacker|hack|data|matrix|android|ai\b|surveillance|megacorp|network|robot|punk)/],
  ["space", /\b(?:space|star|galaxy|planet|orbit|mars|moon|ship|void|interstellar|colony|empire)/],
  ["oppression", /\b(?:rebellion|regime|oppression|resistance|district|war\b|hierarchy|prison|control|tyrant)/],
  ["philosophy", /\b(?:memory|consciousness|identity|truth|soul|meaning|ethics|philosophy|existential)/],
];

function inferArchetype(p: ProtagonistVoiceInput): Archetype {
  const corpus = [p.title, p.author, p.protagonist ?? "", p.protagonistIntro ?? ""].join(" ").toLowerCase();
  for (const [archetype, re] of ARCHETYPE_TERMS) {
    if (re.test(corpus)) return archetype;
  }
  return "default";
}

/**
 * Best ElevenLabs voice ID for a protagonist: curated manual override if known,
 * otherwise a deterministic archetype-pooled choice.
 */
export function resolveVoiceId(p: ProtagonistVoiceInput): string {
  const name = p.protagonist?.trim();
  if (name) {
    const override = MANUAL_OVERRIDES[normalize(name)];
    if (override) return override;
  }
  const basePool = ARCHETYPE_POOLS[inferArchetype(p)] ?? FALLBACK_POOL;
  const seed = [p.id != null ? String(p.id) : "", p.protagonist ?? "", p.title, p.author].join("|");

  // Gender-aware fallback: when the protagonist's gender is inferable, restrict
  // the pool to same-gender voices — preferring the archetype's own voices, then
  // any voice of that gender — so unmapped characters lean correctly instead of
  // getting a random-gender voice. Unknown gender → previous archetype behaviour.
  const gender = inferGender(p);

  // Characters the text does not gender (Murderbot, Yatima) get the neutral
  // voice deliberately, rather than being assigned one by a hash.
  if (gender === "neutral") return HOUSE_VOICE;

  if (gender) {
    const wanted = gender === "female" ? FEMALE_VOICES : MALE_VOICES;
    let pool = basePool.filter((v) => wanted.has(v));
    if (pool.length === 0) pool = FALLBACK_POOL.filter((v) => wanted.has(v));
    if (pool.length > 0) return pool[stableIndex(seed, pool.length)];
  }

  // Gender unknown → the neutral house voice, NOT a draw from a gender-mixed
  // pool. HOUSE_VOICE was declared for exactly this ("used when gender cannot be
  // inferred") but nothing ever reached it: the line below used to hash into
  // basePool, so an unknown character got a coin-flip gender. That is what made
  // 44 of 147 protagonists speak in the wrong one. A neutral voice for a
  // character we have not recorded is a visible, honest default — add the
  // character to PROTAGONIST_GENDER_RAW to give them a gendered voice.
  return HOUSE_VOICE;
}

/**
 * Possessive pronoun for a person, from the SAME determination that picks their
 * voice — so the words and the voice can never disagree.
 *
 * Voice mode is reused for authors, who are not characters in a book, so both
 * surfaces pass a placeholder where a book title goes ("his own work"). That
 * string is displayed on screen AND is part of the voice seed, so the two
 * surfaces having picked different wording gave Philip K. Dick a different male
 * voice on his own page than on the map. Deriving the pronoun here makes the
 * phrase identical wherever it is built, which settles both at once.
 *
 * "their" is the answer when we do not know, which is the right default: it is
 * never wrong about a person, only unspecific.
 */
export function possessivePronounFor(name: string): "his" | "her" | "their" {
  const g = inferGender({ protagonist: name, title: "", author: "" });
  return g === "male" ? "his" : g === "female" ? "her" : "their";
}

export function voiceDisplayName(voiceId: string): string {
  return VOICE_NAMES[voiceId] ?? "Default";
}

/** ElevenLabs voice tuning per archetype (stability / similarity for the agent override). */
export function voiceSettings(p: ProtagonistVoiceInput): { stability: number; similarityBoost: number } {
  switch (inferArchetype(p)) {
    case "cyberpunk":
    case "oppression":
      return { stability: 0.35, similarityBoost: 0.8 };
    case "space":
      return { stability: 0.58, similarityBoost: 0.74 };
    case "philosophy":
      return { stability: 0.7, similarityBoost: 0.7 };
    default:
      return { stability: 0.5, similarityBoost: 0.75 };
  }
}
